import { Group, IconButton, iconSizes, Paper, Stack, Switch, Text } from '@common/ui'
import TrimmedTextInput from './TrimmedTextInput'
import { IconPlus, IconTrash } from '@common/ui/icons'
import { com, kotlin } from '@eidu/web'
import Option = com.eidu.sharedlib.form.definition.Option
import GradeDropDown = com.eidu.sharedlib.form.definition.GradeDropDown
import KtList = kotlin.collections.KtList
import { keyOf, withKey } from '../../util/withKey'
import FormKeyField from './FormKeyField'

type GradeDropDownItemProps = {
  item: GradeDropDown
  onChange?: (item: GradeDropDown) => void
}

const GradeDropDownItem: React.FC<GradeDropDownItemProps> = ({ item, onChange }) => {
  const handleOptionTextChange = (index: number, text: string) => {
    const newOptions = [...item.options.asJsReadonlyArrayView()]
    newOptions[index] = withKey(new Option(text, newOptions[index].items), newOptions[index])
    onChange?.(
      withKey(
        item.copy(
          item.prompt,
          KtList.fromJsArray(newOptions),
          item.key,
          item.required,
          item.unfilledText,
          item.placeholder
        ),
        item
      )
    )
  }

  const addOption = () => {
    const newOptions = [...item.options.asJsReadonlyArrayView(), new Option('New option', KtList.fromJsArray([]))]
    onChange?.(
      item.copy(
        item.prompt,
        KtList.fromJsArray(newOptions),
        item.key,
        item.required,
        item.unfilledText,
        item.placeholder
      )
    )
  }

  const removeOption = (index: number) => {
    const newOptions = item.options.asJsReadonlyArrayView().filter((_, i) => i !== index)
    onChange?.(
      item.copy(
        item.prompt,
        KtList.fromJsArray(newOptions),
        item.key,
        item.required,
        item.unfilledText,
        item.placeholder
      )
    )
  }

  return (
    <Paper withBorder radius="sm" p="xs">
      <Stack gap="md">
        <Group gap="md" wrap="nowrap" align="flex-start">
          <TrimmedTextInput
            style={{ flex: 1 }}
            label="Prompt"
            value={item.prompt ?? ''}
            onChange={(e) =>
              onChange?.(
                item.copy(e.target.value, item.options, item.key, item.required, item.unfilledText, item.placeholder)
              )
            }
            disabled={!onChange}
          />
          <FormKeyField
            value={item.key}
            onChange={(e) =>
              onChange?.(
                item.copy(item.prompt, item.options, e.target.value, item.required, item.unfilledText, item.placeholder)
              )
            }
            disabled={!onChange}
          />
        </Group>
        <Group gap="md" wrap="nowrap" align="center">
          <TrimmedTextInput
            style={{ flex: 1 }}
            label="Unfilled Text"
            value={item.unfilledText ?? ''}
            onChange={(e) =>
              onChange?.(
                item.copy(item.prompt, item.options, item.key, item.required, e.target.value, item.placeholder)
              )
            }
            disabled={!onChange}
          />
          <TrimmedTextInput
            style={{ flex: 1 }}
            label="Placeholder"
            value={item.placeholder ?? ''}
            onChange={(e) =>
              onChange?.(
                item.copy(item.prompt, item.options, item.key, item.required, item.unfilledText, e.target.value)
              )
            }
            disabled={!onChange}
          />
          <Switch
            checked={item.required}
            onChange={(e) =>
              onChange?.(
                item.copy(item.prompt, item.options, item.key, e.target.checked, item.unfilledText, item.placeholder)
              )
            }
            disabled={!onChange}
            label="Required"
          />
        </Group>
        <Stack gap="md">
          <Text size="sm" fw={600}>
            Options
          </Text>
          {item.options.asJsReadonlyArrayView().map((option, index) => (
            <Paper withBorder radius="sm" p="xs" key={keyOf(option)}>
              <Group gap="md" wrap="nowrap">
                <TrimmedTextInput
                  style={{ flex: 1 }}
                  label={`Option ${index + 1}`}
                  value={option.text}
                  onChange={(e) => handleOptionTextChange(index, e.target.value)}
                  disabled={!onChange}
                />
                {onChange && (
                  <IconButton size="sm" aria-label="Remove option" onClick={() => removeOption(index)}>
                    <IconTrash size={iconSizes.sm} stroke={1.5} />
                  </IconButton>
                )}
              </Group>
            </Paper>
          ))}
          {onChange && (
            <IconButton size="sm" aria-label="Add option" onClick={addOption} style={{ alignSelf: 'center' }}>
              <IconPlus size={iconSizes.sm} stroke={1.5} />
            </IconButton>
          )}
        </Stack>
      </Stack>
    </Paper>
  )
}

export default GradeDropDownItem
